import type { CollectionRecord, MemoryCard, MemoryLine, SemanticType } from './model';

export const NEVER_INDEX_SEMANTIC_TYPES: ReadonlySet<SemanticType> = new Set([
  'password',
  'pin',
  'card-number',
  'security-code',
  'id-number',
  'recovery-code',
  'totp-seed',
  'private-key',
  'seed-phrase',
  'secret',
  'secret-answer',
  'api-key',
  'access-token',
  'bank-account',
  'iban',
  'tax-id',
  'social-security-number',
]);

const SENSITIVE_LABEL_PATTERN = /(?:^|\b)(?:password|passphrase|passcode|pin|cvv|cvc|security\s+code|card\s+(?:number|no)|account\s+(?:number|no)|bank\s+account|iban|passport\s+(?:number|no)|document\s+(?:number|no)|id\s+(?:number|no)|social\s+security|ssn|tax\s+(?:id|number)|recovery\s+code|backup\s+code|totp|otp\s+seed|api\s+key|access\s+token|refresh\s+token|session\s+token|bearer\s+token|auth(?:entication|orization)?\s+token|client\s+secret|private\s+key|seed\s+phrase|secret\s+answer)(?:\b|$)/iu;

function normalizeLabelForPolicy(label: string): string {
  return label
    .normalize('NFKC')
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

export interface SearchProjection {
  recordId: string;
  text: string;
  tokens: string[];
}

export function isLineValueIndexable(line: MemoryLine): boolean {
  if (!line.indexable) return false;
  if (NEVER_INDEX_SEMANTIC_TYPES.has(line.semanticType)) return false;
  if (SENSITIVE_LABEL_PATTERN.test(normalizeLabelForPolicy(line.label))) return false;
  return true;
}

export function normalizeSearchText(value: string): string {
  return value
    .normalize('NFKC')
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}@._:/-]+/gu, ' ')
    .trim();
}

export function buildSearchProjection(
  card: MemoryCard,
  collections: ReadonlyMap<string, CollectionRecord> = new Map(),
): SearchProjection {
  const parts: string[] = [card.title];

  for (const collectionId of card.collectionIds) {
    const collection = collections.get(collectionId);
    if (collection) parts.push(collection.name);
  }

  for (const line of card.lines) {
    parts.push(line.label);
    if (isLineValueIndexable(line)) parts.push(line.value);
  }

  const text = normalizeSearchText(parts.filter(Boolean).join(' '));
  return {
    recordId: card.recordId,
    text,
    tokens: [...new Set(text.split(/\s+/).filter(Boolean))],
  };
}

export function searchCards(
  cards: readonly MemoryCard[],
  query: string,
  collections: ReadonlyMap<string, CollectionRecord> = new Map(),
): MemoryCard[] {
  const normalized = normalizeSearchText(query);
  if (!normalized) return [...cards];
  const terms = normalized.split(/\s+/).filter(Boolean);
  return cards.filter((card) => {
    const projection = buildSearchProjection(card, collections);
    return terms.every((term) => projection.text.includes(term));
  });
}
